/**
 * Migrate legacy single-scene localStorage into a default project + workspace.
 * Call once on app load when projects feature is used.
 */

import {
  getDefaultAppState,
  clearAppStateForLocalStorage,
} from "@excalidraw/excalidraw/appState";
import { getNonDeletedElements } from "@excalidraw/element";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { importFromLocalStorage } from "../localStorage";

import {
  hasCurrentWorkspace,
  setCurrentWorkspace,
  listProjects,
  createProject,
  createWorkspace,
} from "./ProjectsStorage";

const MIGRATION_DONE_KEY = "excalidraw-projects-migration-done";
const MIGRATION_LOCK_KEY = "excalidraw-projects-migration-lock";
const MIGRATION_LOCK_MS = 8000;

/** Returns true if migration ran (created default project/workspace from localStorage). */
export async function migrateLegacySceneIfNeeded(): Promise<boolean> {
  try {
    if (typeof localStorage === "undefined") {
      return false;
    }
    if (localStorage.getItem(MIGRATION_DONE_KEY) === "1") {
      return false;
    }
    if (hasCurrentWorkspace()) {
      localStorage.setItem(MIGRATION_DONE_KEY, "1");
      return false;
    }

    const legacy = importFromLocalStorage();
    const hasElements =
      legacy.elements &&
      Array.isArray(legacy.elements) &&
      legacy.elements.length > 0;
    const hasState = legacy.appState != null;

    if (!hasElements && !hasState) {
      localStorage.setItem(MIGRATION_DONE_KEY, "1");
      return false;
    }

    const elements = restoreElements(legacy.elements ?? [], null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }) as ExcalidrawElement[];
    const appState = restoreAppState(
      legacy.appState ?? getDefaultAppState(),
      null,
    );
    const clearedAppState = clearAppStateForLocalStorage(appState);

    const now = Date.now();
    const lockVal = localStorage.getItem(MIGRATION_LOCK_KEY);
    const lockedAt = lockVal ? parseInt(lockVal, 10) : 0;
    if (lockedAt && now - lockedAt < MIGRATION_LOCK_MS) {
      await new Promise((r) => setTimeout(r, 600));
      const existing = await listProjects();
      if (existing.length > 0) {
        const project = existing[0];
        const workspace = await createWorkspace(project.id, null, "Untitled", {
          elements: getNonDeletedElements(elements) as ExcalidrawElement[],
          appState: clearedAppState,
        });
        setCurrentWorkspace(project.id, workspace.id);
        localStorage.setItem(MIGRATION_DONE_KEY, "1");
        return true;
      }
    }

    localStorage.setItem(MIGRATION_LOCK_KEY, String(now));
    try {
      const existing = await listProjects();
      const project =
        existing.length > 0 ? existing[0] : await createProject("Default");
      const workspace = await createWorkspace(project.id, null, "Untitled", {
        elements: getNonDeletedElements(elements) as ExcalidrawElement[],
        appState: clearedAppState,
      });
      setCurrentWorkspace(project.id, workspace.id);
      localStorage.setItem(MIGRATION_DONE_KEY, "1");
      return true;
    } finally {
      localStorage.removeItem(MIGRATION_LOCK_KEY);
    }
  } catch (e) {
    console.error("Projects migration failed", e);
    return false;
  }
}

/** For tests or reset: allow running migration again. */
export function clearMigrationDoneFlag(): void {
  try {
    localStorage.removeItem(MIGRATION_DONE_KEY);
  } catch {
    // noop
  }
}
